import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Target } from "lucide-react";
import { api } from "@/lib/api/client";
import { downloadFile } from "@/lib/api/download";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { EntityPickerField, type PickedEntity } from "@/components/ui/EntityPicker";
import { RowActions } from "@/components/ui/RowActions";
import { useNavigate } from "react-router-dom";

/**
 * Quản lý nhiệm vụ › Danh sách nhiệm vụ (mission.zul) — 15 ô lọc và 15 cột đúng
 * như lưới hệ cũ. Trước đây màn này chỉ có 1 ô tìm theo tên và 6 cột.
 */
type MissionRow = {
  MISSION_ID: number;
  STATUS: number | null;
  MISSION_NAME: string | null;
  FLOW_NAME: string | null;
  CONTENT: string | null;
  TARGET: string | null;
  WEIGHT: number | null;
  FREQUENCE_UPDATE: number | null;
  FIELD_NAME: string | null;
  ORG_ASSIGN_NAME: string | null;
  ASSIGN_NAME: string | null;
  ORG_PERFORM_NAME: string | null;
  UPDATED_DATE: string | null;
  DATE_COMPLETE: string | null;
  MISSION_RESULT: string | null;
};

const STATUS_LABEL: Record<number, string> = {
  1: "Mới giao",
  2: "Đang thực hiện",
  3: "Hoàn thành",
  5: "Tạm dừng",
  7: "Quá hạn",
};
const FREQUENCY_LABEL: Record<number, string> = {
  1: "Hàng tuần",
  2: "Hàng tháng",
  3: "Hàng quý",
  4: "Hàng năm",
};
const MISSION_GROUP_LABEL: Record<number, string> = { 1: "Thường xuyên", 2: "Đột xuất" };
const APPROVED_LABEL: Record<number, string> = { 0: "Chờ phê duyệt", 1: "Đã phê duyệt" };

type Filters = {
  missionName: string;
  content: string;
  fieldId: string;
  frequency: string;
  orgAssign: PickedEntity | null;
  assign: PickedEntity | null;
  orgPerform: PickedEntity | null;
  orgCombination: PickedEntity | null;
  startFrom: string;
  startTo: string;
  completeFrom: string;
  completeTo: string;
  missionGroup: string;
  approved: string;
  sourceId: string;
};

const EMPTY: Filters = {
  missionName: "",
  content: "",
  fieldId: "",
  frequency: "",
  orgAssign: null,
  assign: null,
  orgPerform: null,
  orgCombination: null,
  startFrom: "",
  startTo: "",
  completeFrom: "",
  completeTo: "",
  missionGroup: "",
  approved: "",
  sourceId: "",
};

const toParams = (f: Filters) => ({
  missionName: f.missionName || undefined,
  content: f.content || undefined,
  fieldId: f.fieldId || undefined,
  frequency: f.frequency || undefined,
  orgAssignId: f.orgAssign?.id,
  assignId: f.assign?.id,
  orgPerformId: f.orgPerform?.id,
  orgCombinationId: f.orgCombination?.id,
  startFrom: f.startFrom || undefined,
  startTo: f.startTo || undefined,
  completeFrom: f.completeFrom || undefined,
  completeTo: f.completeTo || undefined,
  missionGroup: f.missionGroup || undefined,
  approved: f.approved || undefined,
  sourceId: f.sourceId || undefined,
});

const day = (v: string | null) => (v ? v.slice(0, 10) : "—");
const txt = (v: string | null) => v ?? "—";

export function MissionListPage() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const { data: fields } = useQuery({
    queryKey: ["mission-fields"],
    queryFn: async () => {
      const { data } = await api.get("/task/mission/fields");
      return (data.data ?? data) as { FIELD_ID: number; FIELD_NAME: string | null }[];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["mission-list", page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/task/mission/list", {
        params: { page, pageSize, ...toParams(applied) },
      });
      return (data.data ?? data) as { items: MissionRow[]; total: number };
    },
  });

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const columns: Column<MissionRow>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "90px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[
            { label: "Xem chi tiết", icon: Eye, onClick: () => nav(`/tasks/${r.MISSION_ID}`) },
          ]}
        />
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "140px",
      render: (r) => (r.STATUS == null ? "—" : (STATUS_LABEL[r.STATUS] ?? `#${r.STATUS}`)),
    },
    {
      key: "name",
      header: "Tên nhiệm vụ",
      width: "280px",
      render: (r) => <span className="font-medium">{txt(r.MISSION_NAME)}</span>,
    },
    { key: "flow", header: "Tên luồng công việc", width: "180px", render: (r) => txt(r.FLOW_NAME) },
    { key: "content", header: "Nội dung nhiệm vụ", width: "240px", render: (r) => txt(r.CONTENT) },
    { key: "target", header: "Mục tiêu nhiệm vụ", width: "220px", render: (r) => txt(r.TARGET) },
    {
      key: "weight",
      header: "Trọng số",
      width: "90px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.WEIGHT ?? "—"}</span>,
    },
    {
      key: "freq",
      header: "Tần suất báo cáo",
      width: "150px",
      render: (r) =>
        r.FREQUENCE_UPDATE == null
          ? "—"
          : (FREQUENCY_LABEL[r.FREQUENCE_UPDATE] ?? `#${r.FREQUENCE_UPDATE}`),
    },
    { key: "field", header: "Lĩnh vực", width: "150px", render: (r) => txt(r.FIELD_NAME) },
    {
      key: "orgAssign",
      header: "Đơn vị giao việc",
      width: "200px",
      render: (r) => txt(r.ORG_ASSIGN_NAME),
    },
    { key: "assign", header: "Người giao việc", width: "170px", render: (r) => txt(r.ASSIGN_NAME) },
    {
      key: "orgPerform",
      header: "Đơn vị thực hiện",
      width: "200px",
      render: (r) => txt(r.ORG_PERFORM_NAME),
    },
    {
      key: "updated",
      header: "Ngày cập nhật",
      width: "130px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.UPDATED_DATE)}</span>,
    },
    {
      key: "complete",
      header: "Ngày hoàn thành",
      width: "140px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.DATE_COMPLETE)}</span>,
    },
    {
      key: "result",
      header: "Kết quả thực hiện",
      width: "220px",
      render: (r) => txt(r.MISSION_RESULT),
    },
  ];

  const select = (
    label: string,
    key: keyof Filters,
    options: Record<number, string> | { value: string; label: string }[],
  ) => ({
    label,
    control: (
      <select
        className={filterInputClass}
        value={filters[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      >
        <option value="">---- Tất cả ----</option>
        {Array.isArray(options)
          ? options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          : Object.entries(options).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
      </select>
    ),
  });

  const text = (label: string, key: keyof Filters, placeholder?: string) => ({
    label,
    control: (
      <input
        className={filterInputClass}
        placeholder={placeholder}
        value={filters[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      />
    ),
  });

  const date = (label: string, key: keyof Filters) => ({
    label,
    control: (
      <input
        type="date"
        className={filterInputClass}
        value={filters[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      />
    ),
  });

  const picker = (label: string, key: keyof Filters, kind: "org" | "staff") => ({
    label,
    control: (
      <EntityPickerField
        kind={kind}
        value={filters[key] as PickedEntity | null}
        onChange={(v) => set(key, v as never)}
      />
    ),
  });

  return (
    <div>
      <PageHeader
        title="Danh sách nhiệm vụ"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} nhiệm vụ`}
        breadcrumb="Quản lý nhiệm vụ › Danh sách nhiệm vụ"
      />
      <SearchPanel
        fields={[
          text("Tên nhiệm vụ", "missionName"),
          text("Nội dung nhiệm vụ", "content"),
          select(
            "Lĩnh vực",
            "fieldId",
            (fields ?? []).map((f) => ({
              value: String(f.FIELD_ID),
              label: f.FIELD_NAME ?? String(f.FIELD_ID),
            })),
          ),
          select("Tần suất báo cáo", "frequency", FREQUENCY_LABEL),
          picker("Đơn vị giao việc", "orgAssign", "org"),
          picker("Người giao việc", "assign", "staff"),
          picker("Đơn vị thực hiện", "orgPerform", "org"),
          picker("Đơn vị phối hợp", "orgCombination", "org"),
          date("Ngày bắt đầu từ", "startFrom"),
          date("đến", "startTo"),
          date("Ngày hoàn thành từ", "completeFrom"),
          date("đến", "completeTo"),
          select("Loại nhiệm vụ", "missionGroup", MISSION_GROUP_LABEL),
          select("Trạng thái phê duyệt", "approved", APPROVED_LABEL),
          text("Nguồn gốc nhiệm vụ", "sourceId", "ID nguồn"),
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
        onExport={() =>
          downloadFile("/task/mission/list/export", toParams(applied), "danh-sach-nhiem-vu.csv")
        }
      />
      <div className="overflow-x-auto">
        <div className="min-w-[2600px]">
          <DataTable<MissionRow>
            columns={columns}
            rows={data?.items ?? []}
            keyBy={(r) => r.MISSION_ID}
            isLoading={isLoading}
            empty={
              <>
                <Target className="inline h-4 w-4 mr-1" /> Không có dữ liệu!
              </>
            }
            dense
          />
        </div>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
